import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { LEAD_BASED_PAINT } from '@shared/utils/app-static-data';
import { CommonDataService } from 'app/services/common/common-data.service';
import { SharedService } from 'app/services/core/shared.service';
import { ToastrService } from 'ngx-toastr';
import { IAccountAffiliation } from 'app/models/common/accountAffiliation';
import { AddEditAtpComponent } from 'app/components/atp/add-edit-atp/add-edit-atp.component';
import { FirmAddEditComponent } from 'app/components/firm/add-edit/firm-add-edit.component';
import { AddEditIndividualComponent } from 'app/components/individual/add-edit-individual/add-edit-individual.component';
import { AtpService } from 'app/services/atp/atp.service';
import { FirmService } from 'app/services/firm/firm-service';
import { PersonService } from 'app/services/person/person.service';

@Component({
  selector: 'app-pending-access-updates',
  templateUrl: './pending-access-updates.component.html',
  styleUrls: ['./pending-access-updates.component.scss']
})
export class PendingAccessUpdatesComponent implements OnInit {
  public uiData = LEAD_BASED_PAINT;
  public loading: boolean = true;
  public pendingAccessUpdates: IAccountAffiliation[] = [];
  public pendingAccessUpdatesDataSource: MatTableDataSource<any>;
  
  public pendingAccessTable = {
    layout: {
      columns: ['atpId', 'firmId', 'personId', 'userName', 'userID', 'status', 'requestedStatus', 'lastUpdatedBy', 'actions'],
      container: [
        { displayName: 'User Name', columnName: 'userName', type: 'string', size: '15' },
        { displayName: 'User Email', columnName: 'userID', type: 'string', size: '20' },
        { displayName: 'Status', columnName: 'status', type: 'string', size: '12' },
        { displayName: 'Requested Status', columnName: 'requestedStatus', type: 'string', size: '12' },
        { displayName: 'Requested By', columnName: 'lastUpdatedBy', type: 'string', size: '15' }
      ],
      data: []
    }
  };
  public pendingAccessTablePageSize = 10;
  public filterString: string = '';

  @ViewChild('PendingAccessTablePaginator') pendingAccessPaginator: MatPaginator;
  @ViewChild('PendingAccessTableSort') pendingAccessSort: MatSort;

  constructor(
    public commonService: CommonDataService,
    public sharedService: SharedService,
    private readonly toastr: ToastrService,
    private readonly dialog: MatDialog,
    private readonly atpService: AtpService,
    private readonly firmService: FirmService,
    private readonly personService: PersonService
  ) { }

  ngOnInit(): void {
    this.loadData();
  }

  loadData(): void {
    this.loading = true;
    this.commonService.getPendingAccessUpdates().subscribe(result => {
      this.pendingAccessUpdates = result;
      this.pendingAccessTable.layout.data = result.map(r => ({
        ...r,
        atpId: r.atp?.id || null,
        atpName: r.atp?.name || '',
        firmId: r.firm?.id || null,
        firmName: r.firm?.name || '',
        personId: r.person?.id || null,
      }));
      this.pendingAccessUpdatesDataSource = new MatTableDataSource<any>(this.pendingAccessTable.layout.data);
      this.pendingAccessUpdatesDataSource.paginator = this.pendingAccessPaginator;
      this.pendingAccessUpdatesDataSource.sort = this.pendingAccessSort;
      this.loading = false;
    }, error => {
      this.toastr.error('Error loading pending access updates', error);
      this.loading = false;
    });
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.pendingAccessUpdatesDataSource.filter = filterValue.trim().toLowerCase();

    if (this.pendingAccessUpdatesDataSource.paginator) {
      this.pendingAccessUpdatesDataSource.paginator.firstPage();
    }
  }

  openAtp(atpId: number): void {
    if (!atpId) return;
    
    this.atpService.getATPById(atpId).subscribe(result => {
      const dialogRef = this.dialog.open(AddEditAtpComponent, {
        width: '90%',
        data: { atp: result },
      });
      dialogRef.afterClosed().subscribe(() => {
        this.loadData();
      });
    }, error => {
      this.toastr.error('Error loading ATP', error);
    });
  }

  openFirm(firmId: number): void {
    if (!firmId) return;
    
    this.firmService.getFirm(firmId).subscribe(result => {
      const dialogRef = this.dialog.open(FirmAddEditComponent, {
        width: '90%',
        data: result,
      });
      dialogRef.afterClosed().subscribe(() => {
        this.loadData();
      });
    }, error => {
      this.toastr.error('Error loading Firm', error);
    });
  }

  openPerson(personId: number): void {
    if (!personId) return;

    this.personService.getPersonById(personId).subscribe(result => {
      const dialogRef = this.dialog.open(AddEditIndividualComponent, {
        width: '90%',
        data: { person: result },
      });
      dialogRef.afterClosed().subscribe(() => {
        this.loadData();
      });
    }, error => {
      this.toastr.error('Error loading Individual', error);
    });
  }

  toggleStatus(row: any): void {
    const newStatus = row.status === 'Active' ? 'Inactive' : 'Active';
    const affiliation: IAccountAffiliation = {
      id: row.id,
      userID: row.userID,
      firm: row.firm,
      atp: row.atp,
      status: newStatus,
      requestedStatus: newStatus,
      person: row.person,
      lastUpdatedBy: row.lastUpdatedBy,
      userName: row.userName
    };
    
    this.commonService.saveAccountAffiliation(affiliation).subscribe(() => {
      this.toastr.success('Status updated successfully');
      this.loadData();
    }, error => {
      this.toastr.error('Error updating status', error);
    });
  }

  displayLimit(value: string, size: string): string {
    if (!value) return '';
    const maxLength = parseInt(size, 10) * 2;
    return value.length > maxLength ? value.substring(0, maxLength) + '...' : value;
  }
}
